import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Botao } from './base';
import { Formulario, type DefCampo } from './formulario';
import { Modal } from './sobreposicao';
import { get, post } from '../nucleo/api';

/**
 * Fase 2 (W25, K14): os campos essenciais do catálogo de Serviços (Cadastros) e de Produtos (Estoque), num lugar só. A tela
 * do catálogo monta o formulário completo com eles (`Servicos.tsx`, `Produtos.tsx`) e o cadastro rápido de outro módulo (o
 * item da compra direta) monta só eles, com os mesmos rótulos, opções e rota de gravação do catálogo. Os rótulos vêm do
 * namespace do módulo dono (`cadastros`, `estoque`), que é quem passa o `t`.
 */
export const USOS_SERVICO = ['venda', 'compra', 'ambos'] as const;
export const TIPOS_COBRANCA = ['recorrente', 'avulso'] as const;

type CampoServico = 'nome' | 'codigo' | 'uso' | 'tipo_cobranca' | 'preco_venda_centavos' | 'custo_centavos';
type CampoProduto = 'codigo' | 'nome' | 'unidade' | 'custo_centavos' | 'preco_centavos' | 'ncm';

/** Campo do catálogo de Serviços pelo nome (`t` do namespace `cadastros`). */
export function campoServico(t: TFunction, nome: CampoServico, desabilitado = false): DefCampo {
  switch (nome) {
    case 'nome':
      return { nome, rotulo: t('servicos.campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia', desabilitado };
    case 'codigo':
      return { nome, rotulo: t('servicos.campos.codigo'), tipo: 'texto', largura: 'meia', desabilitado };
    case 'uso':
      return { nome, rotulo: t('servicos.campos.uso'), tipo: 'selecao', obrigatorio: true, largura: 'terco', opcoes: USOS_SERVICO.map((u) => ({ valor: u, rotulo: t(`servicos.usos.${u}`) })), desabilitado };
    case 'tipo_cobranca':
      return { nome, rotulo: t('servicos.campos.tipoCobranca'), tipo: 'selecao', obrigatorio: true, largura: 'terco', opcoes: TIPOS_COBRANCA.map((c) => ({ valor: c, rotulo: t(`servicos.cobrancas.${c}`) })), desabilitado };
    case 'preco_venda_centavos':
      return { nome, rotulo: t('servicos.campos.preco'), tipo: 'dinheiro', largura: 'terco', desabilitado };
    case 'custo_centavos':
      return { nome, rotulo: t('servicos.campos.custo'), tipo: 'dinheiro', largura: 'terco', desabilitado };
  }
}

/** Campo do catálogo de Produtos pelo nome (`t` do namespace `estoque`; `unidades` da lista do núcleo). */
export function campoProduto(t: TFunction, nome: CampoProduto, unidades: { valor: string; rotulo: string }[] = []): DefCampo {
  switch (nome) {
    case 'codigo':
      return { nome, rotulo: t('campos.codigo'), tipo: 'texto', obrigatorio: true, largura: 'terco' };
    case 'nome':
      return { nome, rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia' };
    case 'unidade':
      return { nome, rotulo: t('campos.unidade'), tipo: 'selecao', obrigatorio: true, largura: 'terco', opcoes: unidades };
    case 'custo_centavos':
      return { nome, rotulo: t('campos.custoMedio'), tipo: 'dinheiro', largura: 'terco', ajuda: t('ajuda.custoMedio') };
    case 'preco_centavos':
      return { nome, rotulo: t('campos.preco'), tipo: 'dinheiro', largura: 'terco' };
    // Lote 09/10 (B3): NCM (8 dígitos; o servidor aceita com pontos), também no cadastro rápido, como no antigo.
    case 'ncm':
      return { nome, rotulo: t('campos.ncm'), tipo: 'texto', largura: 'terco', max: 20, placeholder: '0000.00.00', ajuda: t('ajuda.ncm') };
  }
}

/** Unidades de medida do núcleo (a mesma consulta e chave do Estoque). */
export function useUnidadesDoNucleo(habilitado = true) {
  const q = useQuery({
    queryKey: ['estoque', 'catalogos'],
    queryFn: () => get<{ unidades: { codigo: string; nome: string }[] }>('/nucleo/catalogos'),
    staleTime: 300_000,
    enabled: habilitado,
  });
  return (q.data?.unidades ?? []).map((u) => ({ valor: u.codigo, rotulo: `${u.codigo} (${u.nome})` }));
}

export interface CriadoRapido {
  id: string;
  nome: string;
  /** Custo informado (o item da compra usa como valor unitário sugerido). */
  custo_centavos: number;
}

interface PropsRapido {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  aoCriar: (c: CriadoRapido) => void;
}

/** Cadastro rápido de serviço de compra (uso "compra", cobrança avulsa); o resto fica para a tela do catálogo. */
export function CadastroRapidoServico({ aberto, aoFechar, titulo, aoCriar }: PropsRapido) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const campos: DefCampo[] = (['nome', 'codigo', 'uso', 'tipo_cobranca', 'custo_centavos', 'preco_venda_centavos'] as const).map((c) => campoServico(t, c));
  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo={titulo} largura="lg" rodape={<Rodape formulario="form-servico-rapido" aoFechar={aoFechar} salvar={tn('comum.salvar')} cancelar={tn('comum.cancelar')} />}>
      {aberto && (
        <Formulario
          id="form-servico-rapido"
          campos={campos}
          entidade="cadastros.servico"
          inicial={{ uso: 'compra', tipo_cobranca: 'avulso', custo_centavos: 0, preco_venda_centavos: 0 }}
          aoEnviar={async (v) => {
            const corpo = {
              nome: v.nome,
              codigo: (v.codigo as string) || null,
              uso: v.uso,
              tipo_cobranca: v.tipo_cobranca,
              agendavel: false,
              ativo: true,
              preco_venda_centavos: (v.preco_venda_centavos as number | null) ?? 0,
              custo_centavos: (v.custo_centavos as number | null) ?? 0,
              extras: v.extras ?? {},
            };
            const r = await post<{ id: string }>('/cadastros/servicos', corpo);
            aoCriar({ id: r.id, nome: String(v.nome), custo_centavos: corpo.custo_centavos });
            aoFechar();
          }}
        />
      )}
    </Modal>
  );
}

/**
 * Lote 09/10 (C3): cadastro rápido de serviço CONTRATADO (Cadastros > Serviços > Serviços), o único catálogo do item da
 * compra de serviço: nome e valor de referência; o resto fica para a tela do cadastro.
 */
export function CadastroRapidoServicoContratado({ aberto, aoFechar, titulo, aoCriar }: PropsRapido) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('contratados.campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia', max: 200 },
    { nome: 'valor_referencia_centavos', rotulo: t('contratados.campos.valorReferencia'), tipo: 'dinheiro', largura: 'terco', ajuda: t('contratados.ajuda.valorReferencia') },
  ];
  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo={titulo} largura="lg" rodape={<Rodape formulario="form-contratado-rapido" aoFechar={aoFechar} salvar={tn('comum.salvar')} cancelar={tn('comum.cancelar')} />}>
      {aberto && (
        <Formulario
          id="form-contratado-rapido"
          campos={campos}
          inicial={{}}
          aoEnviar={async (v) => {
            const valor = (v.valor_referencia_centavos as number | null | undefined) ?? null;
            const r = await post<{ id: string }>('/cadastros/servicos-contratados', { nome: v.nome, valor_referencia_centavos: valor, ativo: true });
            aoCriar({ id: r.id, nome: String(v.nome), custo_centavos: valor ?? 0 });
            aoFechar();
          }}
        />
      )}
    </Modal>
  );
}

/** Cadastro rápido de produto do Estoque (código, nome, unidade, custo, preço e NCM); o resto fica para a tela do catálogo. */
export function CadastroRapidoProduto({ aberto, aoFechar, titulo, aoCriar }: PropsRapido) {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const unidades = useUnidadesDoNucleo(aberto);
  const campos: DefCampo[] = (['codigo', 'nome', 'unidade', 'custo_centavos', 'preco_centavos', 'ncm'] as const).map((c) => campoProduto(t, c, unidades));
  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo={titulo} largura="lg" rodape={<Rodape formulario="form-produto-rapido" aoFechar={aoFechar} salvar={tn('comum.salvar')} cancelar={tn('comum.cancelar')} />}>
      {aberto && (
        <Formulario
          id="form-produto-rapido"
          campos={campos}
          entidade="estoque.produto"
          inicial={{ unidade: 'un', custo_centavos: 0, preco_centavos: 0 }}
          aoEnviar={async (v) => {
            const corpo = {
              codigo: v.codigo,
              nome: v.nome,
              unidade: v.unidade,
              custo_centavos: (v.custo_centavos as number | null) ?? 0,
              preco_centavos: (v.preco_centavos as number | null) ?? 0,
              ncm: (v.ncm as string | undefined)?.trim() || null,
              estoque_minimo: 0,
              controla_lote: false,
              ativo: true,
              extras: v.extras ?? {},
            };
            const r = await post<{ id: string }>('/estoque/produtos', corpo);
            aoCriar({ id: r.id, nome: String(v.nome), custo_centavos: corpo.custo_centavos });
            aoFechar();
          }}
        />
      )}
    </Modal>
  );
}

function Rodape({ formulario, aoFechar, salvar, cancelar }: { formulario: string; aoFechar: () => void; salvar: string; cancelar: string }) {
  return (
    <>
      <Botao variante="fantasma" onClick={aoFechar}>
        {cancelar}
      </Botao>
      <Botao variante="primario" type="submit" form={formulario}>
        {salvar}
      </Botao>
    </>
  );
}
